// src/pages/BasicInfoTab.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Input, ColorPicker, Select, Form, message } from "antd";
import MapComponent from "../../components/commons/MapComponent";
import { useAuth } from "../../contexts/AppContext";
import { describeColor } from "../../components/themeEditor/utils";

/**
 * BasicInfoTab —— 场馆基础配置（名称、主题色、实时信息类型、地理位置）
 */
const GOOGLE_KEY = "AIzaSyD63grvHYISPK4dLfyj5kKmC6MR_wQPJ80";
const VLINE_ENABLED_VENUE_IDS = new Set([
  "ts_yarrawonga_visitor_centre",
  "ts_warrnambool_info_centre",
  "ts_swan_hill_information_centre",
]);

const BasicInfoTab = ({ formData, onChange, onNested }) => {
  const [form] = Form.useForm();
  const { selectedRole } = useAuth();
  const venueId = selectedRole?.id || "";
  const iframeRef = useRef(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const allowVLine =
    VLINE_ENABLED_VENUE_IDS.has(venueId) ||
    VLINE_ENABLED_VENUE_IDS.has(formData?.id);
  const liveInfoOptions = useMemo(
    () => [
      { label: "flight", value: "flight" },
      ...(allowVLine ? [{ label: "v/Line", value: "vline" }] : []),
      { label: "tide", value: "tide" },
    ],
    [allowVLine]
  );

  // ---- helpers for debug logs ----
  const dbg = useCallback((msg, extra = undefined) => {
    // 所有调试输出统一前缀，便于过滤
    if (extra !== undefined) {
      console.log(`[BasicInfoTab] ${msg}`, extra);
    } else {
      console.log(`[BasicInfoTab] ${msg}`);
    }
  }, []);

  // 仅用于浅拷贝打印，避免循环引用
  const safeClone = useCallback((obj) => {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch {
      return obj;
    }
  }, []);

  const themeConfiguration =
    formData?.theme && typeof formData.theme === "object"
      ? formData.theme
      : null;

  const touchscreenPreviewOrigin = useMemo(() => {
    if (process.env.REACT_APP_TOUCHSCREEN_PREVIEW_ORIGIN) {
      return process.env.REACT_APP_TOUCHSCREEN_PREVIEW_ORIGIN;
    }
    if (process.env.NODE_ENV === "development") {
      return "http://localhost:3000";
    }
    if (venueId) {
      return `https://${venueId}.touchandexplore.com`;
    }
    return "https://touchscreen.platypus360.com";
  }, [venueId]);

  const previewSrc = useMemo(() => {
    if (!venueId) return "about:blank";
    try {
      const resolved = new URL(touchscreenPreviewOrigin);
      const params = new URLSearchParams();
      params.set("venue_id", venueId);
      params.set("preview", "1");

      // The web build's route (App.js) is a BrowserRouter catch-all that renders
      // MainPage regardless of pathname, so no path segment is actually required —
      // and one shouldn't be added back. touchandexplore.com is a plain S3/CloudFront
      // static site with no SPA-fallback (404/403 -> index.html) configured, so any
      // path beyond "/" 404s at the CDN before React Router ever runs. Root + query
      // string is the only URL shape that's guaranteed to actually load.
      const url = `${resolved.origin}/?${params.toString()}`;
      dbg("Built previewSrc", url);
      return url;
    } catch (err) {
      console.warn("[BasicInfoTab] failed to build preview src", err);
      return "about:blank";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchscreenPreviewOrigin, venueId, dbg]);

  const previewTargetOrigin = useMemo(() => {
    try {
      const parsed = new URL(previewSrc);
      const origin = parsed.origin;
      if (
        origin.startsWith("http://localhost") ||
        origin.startsWith("http://127.") ||
        origin.startsWith("http://192.168.")
      ) {
        dbg("Using wildcard targetOrigin (*) for local preview");
        return "*";
      }
      dbg("Using strict targetOrigin", origin);
      return origin;
    } catch {
      dbg("Failed to parse previewSrc for origin, fallback *");
      return "*";
    }
  }, [previewSrc, dbg]);

  // 给 iframe 发送主题（每次颜色变化都触发）
  const postThemeToPreview = useCallback(() => {
    const iframeEl = iframeRef.current;

    if (!themeConfiguration) {
      dbg("postThemeToPreview skipped: themeConfiguration is null/undefined");
      return;
    }
    if (!iframeEl) {
      dbg("postThemeToPreview skipped: iframeRef.current is null");
      return;
    }
    if (!iframeEl.contentWindow) {
      dbg("postThemeToPreview skipped: iframe.contentWindow missing (not loaded yet?)");
      return;
    }

    const payload = { ...themeConfiguration };
    try {
      dbg("Posting theme to preview (postMessage)", {
        targetOrigin: previewTargetOrigin,
        payload: safeClone(payload),
      });
      iframeEl.contentWindow.postMessage(payload, previewTargetOrigin);
    } catch (err) {
      console.warn("[BasicInfoTab] failed to post theme preview", err);
    }
  }, [themeConfiguration, previewTargetOrigin, dbg, safeClone]);

  // iframe onLoad
  const handlePreviewLoad = useCallback(() => {
    dbg("iframe onLoad fired");
    setPreviewLoaded(true);
    postThemeToPreview(); // 立即发一次
  }, [postThemeToPreview, dbg]);

  // 每次 previewSrc 改变时，重置 loaded 状态
  useEffect(() => {
    dbg("previewSrc changed, reset previewLoaded=false");
    setPreviewLoaded(false);
  }, [previewSrc, dbg]);

  // 保险：iframe 已加载时，每次 themeConfiguration 变化后，延时再发一次
  useEffect(() => {
    if (!previewLoaded) {
      dbg("theme changed but iframe not loaded yet; will send after load");
      return;
    }
    dbg("theme changed & iframe loaded -> schedule postMessage in 80ms", safeClone(themeConfiguration));
    const timer = setTimeout(() => {
      postThemeToPreview();
    }, 80);
    return () => clearTimeout(timer);
  }, [previewLoaded, themeConfiguration, postThemeToPreview, dbg, safeClone]);

  // 进一步：监听 window 的 message（可观察子页面是否有 ACK）
  useEffect(() => {
    const onMsg = (e) => {
      // 这里不校验来源，纯调试
      if (e?.data?.type === "PREVIEW_THEME_ACK") {
        dbg("Received PREVIEW_THEME_ACK from iframe", e.data);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [dbg]);

  // 确保 landing.geoLocation 存在
  useEffect(() => {
    if (!formData?.landing?.geoLocation) {
      dbg("No landing.geoLocation found, set default -9.45,147.18");
      onNested("landing", "geoLocation", { latitude: -9.45, longitude: 147.18 });
    }
  }, [formData, onNested, dbg]);

  const geo = formData?.landing?.geoLocation || { latitude: "", longitude: "" };

  // --- 每次颜色选择时，立刻发消息（确保“修改任何color都要发请求给iframe”） ---
  const handleColorChange = useCallback(
    (key, color) => {
      const val = color?.toRgbString ? color.toRgbString() : color;
      dbg(`ColorPicker onChange: ${key} -> ${val}`);
      onNested("theme", key, val);

      // 立刻尝试发送一遍（即使 iframe 还没 onLoad，也会在 onLoad 再发）
      // 这样可以在加载完成前的连续更改，最后至少有一次最新值被送达
      setTimeout(() => {
        postThemeToPreview();
      }, 0);
    },
    [onNested, postThemeToPreview, dbg]
  );

  return (
    <div className="space-y-6">
      {/* ========== 基础信息 ========== */}
      <section className="bg-white border border-gray-20 p-10 shadow">
        <h2 className="text-[1.2rem] font-semibold text-gray-800 mb-4">
          Basic Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block font-medium text-gray-700 mb-1">
              Venue Name
            </label>
            <Input
              value={formData.name}
              onChange={(e) => onChange("name", e.target.value)}
              size="large"
              disabled
            />
          </div>
          <div>
            <label className="block font-medium text-gray-700 mb-1">
              Alias
            </label>
            <Input
              value={formData.alias}
              onChange={(e) => onChange("alias", e.target.value)}
              placeholder="Enter alias"
              size="large"
            />
          </div>
        </div>
      </section>

      {/* ========== 主题色（修改即触发 postMessage） ========== */}
      <section className="bg-white border border-gray-200 p-10 shadow">
        <h2 className="text-[1.2rem] font-semibold text-gray-800 mb-4">
          Theme Colours
        </h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {["standard", "light", "dark"].map((key) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                  {key} colour
                </label>
                <ColorPicker
                  value={formData.theme?.[key]}
                  showText={(color) => describeColor(color.toHexString())}
                  onChange={(color) => handleColorChange(key, color)}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center">
            <div
              className="w-full max-w-[340px] min-w-[260px] border border-gray-200 shadow-inner bg-gray-100 overflow-hidden"
              style={{ aspectRatio: "9 / 16" }}
            >
              {previewSrc === "about:blank" ? (
                <div className="w-full h-full flex items-center justify-center text-sm text-gray-500 px-6 text-center">
                  Select a venue to preview the touchscreen theme.
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  title="Touchscreen Preview"
                  // src={previewSrc}
                  src={previewSrc}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock allow-popups allow-top-navigation-by-user-activation"
                  className="w-full h-full border-0"
                  onLoad={handlePreviewLoad}
                  onError={() => dbg("iframe onError")}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== 实时信息类型 ========== */}
      <section className="bg-white border border-gray-200 p-10 shadow">
        <h2 className="text-[1.2rem] font-semibold text-gray-800 mb-4">
          Live Information Type
        </h2>
        <Select
          value={
            Array.isArray(formData.landing?.liveInfo)
              ? formData.landing.liveInfo[0]
              : formData.landing?.liveInfo || undefined
          }
          onChange={(value) => {
            dbg("LiveInfo changed", value);
            onNested("landing", "liveInfo", value);
          }}
          options={liveInfoOptions}
          className="w-full"
          size="large"
        />
      </section>

      {/* ========== 地理位置 ========== */}
      <section className="bg-white border border-gray-200 p-10 shadow">
        <h2 className="text-[1.2rem] font-semibold text-gray-800 mb-4">
          Geo Location
        </h2>
        <p className="text-gray-500 mb-4 text-sm">
          Select or enter the coordinates for this venue. The map marker can be
          clicked or dragged to update the location.
        </p>

        <Form layout="vertical" form={form}>
          <Form.Item label="Select on Map">
            <MapComponent
              apiKey={GOOGLE_KEY}
              initialGeolocation={{
                latitude: geo.latitude ?? -9.45,
                longitude: geo.longitude ?? 147.18,
                defaultZoom: 13,
              }}
              onLocationSelect={({ latitude, longitude }) => {
                const lat = +Number(latitude).toFixed(6);
                const lng = +Number(longitude).toFixed(6);
                dbg("Map location selected", { lat, lng });
                onNested("landing", "geoLocation", {
                  latitude: lat,
                  longitude: lng,
                });
                message.success("Map location updated successfully");
              }}
              externalPosition={{
                latitude: geo.latitude,
                longitude: geo.longitude,
              }}
            />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Form.Item label="Latitude">
              <Input
                value={geo.latitude}
                type="number"
                placeholder="e.g. -9.45"
                onChange={(e) =>
                  onNested("landing", "geoLocation", {
                    ...geo,
                    latitude: parseFloat(e.target.value),
                  })
                }
              />
            </Form.Item>
            <Form.Item label="Longitude">
              <Input
                value={geo.longitude}
                type="number"
                placeholder="e.g. 147.18"
                onChange={(e) =>
                  onNested("landing", "geoLocation", {
                    ...geo,
                    longitude: parseFloat(e.target.value),
                  })
                }
              />
            </Form.Item>
          </div>
        </Form>
      </section>
    </div>
  );
};

export default BasicInfoTab;
